import api from './axiosInstance';

export interface ApprovalRequest {
  _id: string;
  requesterId: {
    _id: string;
    name: string;
    email: string;
  };
  requesterName: string;
  requesterEmail: string;
  approverRoleId: {
    _id: string;
    name: string;
  };
  resourceType: string;
  resourceId?: string;
  action: 'create' | 'update' | 'delete';
  requestData: Record<string, any>;
  originalData?: Record<string, any>;
  status: 'pending' | 'approved' | 'rejected';
  approverId?: {
    _id: string;
    name: string;
    email: string;
  };
  approverName?: string;
  approverEmail?: string;
  approverComments?: string;
  approvedAt?: string;
  rejectedAt?: string;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, any>;
}

export interface ApprovalStats {
  totalPending: number;
  totalApproved: number;
  totalRejected: number;
  byResourceType: {
    resourceType: string;
    count: number;
  }[];
}

export interface ApprovalRequestsResponse {
  success: boolean;
  message: string;
  data: ApprovalRequest[];
  meta: {
    page: number;
    limit: number;
    totalPages: number;
    totalDocs: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
  };
}

export interface ApprovalRequestResponse {
  success: boolean;
  message: string;
  data: ApprovalRequest;
}

export interface ApprovalStatsResponse {
  success: boolean;
  message: string;
  data: ApprovalStats;
}

/**
 * Get all approval requests with optional filters
 */
export const getApprovalRequests = async (params?: {
  status?: 'pending' | 'approved' | 'rejected';
  resourceType?: string;
  action?: 'create' | 'update' | 'delete';
  search?: string;
  page?: number;
  limit?: number;
}): Promise<ApprovalRequestsResponse> => {
  const response = await api.get('/approvals', { params });
  return response.data;
};

/**
 * Get pending approvals for current user's role
 */
export const getPendingApprovals = async (params?: {
  page?: number;
  limit?: number;
}): Promise<ApprovalRequestsResponse> => {
  const response = await api.get('/approvals/pending', { params });
  return response.data;
};

/**
 * Get current user's approval requests
 */
export const getMyApprovalRequests = async (params?: {
  page?: number;
  limit?: number;
  status?: 'pending' | 'approved' | 'rejected';
}): Promise<ApprovalRequestsResponse> => {
  const response = await api.get('/approvals/my-requests', { params });
  return response.data;
};

/**
 * Get approval request by ID
 */
export const getApprovalRequestById = async (id: string): Promise<ApprovalRequestResponse> => {
  const response = await api.get(`/approvals/${id}`);
  return response.data;
};

/**
 * Approve an approval request
 */
export const approveRequest = async (
  id: string,
  comments?: string
): Promise<ApprovalRequestResponse> => {
  if (!id || id === 'undefined') {
    throw new Error('Invalid approval request ID');
  }
  
  const response = await api.post(`/approvals/${id}/approve`, { comments });
  return response.data;
};

/**
 * Reject an approval request (rejection reason is REQUIRED)
 */
export const rejectRequest = async (
  id: string,
  comments: string
): Promise<ApprovalRequestResponse> => {
  if (!id || id === 'undefined') {
    throw new Error('Invalid approval request ID');
  }
  
  if (!comments || comments.trim().length === 0) {
    throw new Error('Rejection reason is required');
  }
  
  const response = await api.post(`/approvals/${id}/reject`, { comments });
  return response.data;
};

/**
 * Delete an approval request (only pending, only by requester)
 */
export const deleteApprovalRequest = async (id: string): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/approvals/${id}`);
  return response.data;
};

/**
 * Get approval statistics
 */
export const getApprovalStats = async (params?: {
  approverRoleId?: string;
  requesterId?: string;
}): Promise<ApprovalStatsResponse> => {
  const response = await api.get('/approvals/stats', { params });
  return response.data;
};

